import React, { useState, useMemo, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import gql from "graphql-tag";
import _ from "lodash";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Typography,
  Select,
  MenuItem,
  Switch,
  FormControl,
  InputLabel,
  Avatar,
  Chip,
} from "@material-ui/core";
import SelectDepartment from "../../components/SelectDepartment/SelectDepartment";
import { NOTIFICATION_FREQUENCY } from "../../utils/const";
import { AUTH_DICT } from "../../utils/authorization";
import { useAuth } from "../../context/authContext";
import Icon from "@mdi/react";
import { mdiAccount, mdiCreditCardOutline } from "@mdi/js";
import styles from "./Users.module.scss";
import { removeTypenameDeep } from "../../utils/misc";

const AddEditUserDialog = ({
  open,
  handleClose,
  user: dialogUser,
  form,
  resetForm,
  formErrors,
  handleChange,
  handleToggleCheckBox,
  handleSubmit,
  handleSelectPicture,
  handleCreateUserCancel,
  plans,
  invoice,
  invoiceDataLoading,
}) => {
  const { auth } = useAuth();
  const authOptions = useMemo(() => {
    const currentLevel = AUTH_DICT[auth.auth];
    const arr = [];
    for (let key in AUTH_DICT) {
      if (AUTH_DICT[key] >= currentLevel && !key.includes("vantage")) arr.push(key);
    }
    return arr;
  }, [auth]);

  const [profilePictureTempUrl, setProfilePictureTempUrl] = useState("");

  const changeAvatar = async (e, form) => {
    const existingProfilePicture = _.get(form, "profilePicture");
    let profilePictureData = await handleSelectPicture(e, existingProfilePicture);

    const { url, file } = profilePictureData;

    setProfilePictureTempUrl(url);
    handleChange({
      target: {
        name: "profilePicture",
        value: file,
      },
    });
  };
  useEffect(() => {
    if (dialogUser) {
      const { id, name, auth, position, email, plan, notificationSettings, profilePicture } = dialogUser;
      const notificationsWithoutTypename = removeTypenameDeep(notificationSettings);
      let editForm = {
        id,
        firstName: name.first,
        lastName: name.last,
        position,
        email,
        password: "",
        plan: plan.map(({ id }) => id),
        notificationSettings: notificationsWithoutTypename,
        auth,
        profilePicture,
      };
      resetForm(editForm);
    }
  }, [dialogUser]);

  useEffect(() => {
    if (!open) {
      setProfilePictureTempUrl("");
    }
  }, [open]);

  const isPayingUser = dialogUser?.stripeCustomerId;

  return (
    <>
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        onExited={() => resetForm()}
      >
        <DialogTitle>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span>{dialogUser ? "Edit" : "Create"} User</span>
            {isPayingUser && (
              <Chip
                icon={<Icon path={mdiCreditCardOutline} size={0.7} />}
                label="Paying User"
                color="primary"
                size="small"
                style={{ marginLeft: 8 }}
              />
            )}
          </div>
        </DialogTitle>
        <DialogContent>
          <TextField
            label="First Name"
            name="firstName"
            fullWidth
            variant="outlined"
            margin="normal"
            value={form.firstName || ""}
            onChange={handleChange}
            helperText={formErrors.firstName}
            error={Boolean(formErrors.firstName)}
          />
          <TextField
            label="Last Name"
            name="lastName"
            fullWidth
            variant="outlined"
            margin="normal"
            value={form.lastName || ""}
            onChange={handleChange}
            helperText={formErrors.lastName}
            error={Boolean(formErrors.lastName)}
          />
          <TextField
            label="Email"
            name="email"
            fullWidth
            variant="outlined"
            margin="normal"
            value={form.email || ""}
            onChange={handleChange}
            helperText={formErrors.email}
            error={Boolean(formErrors.email)}
          />
          {/* <TextField
            type="password"
            label={dialogUser ? "New Password" : "Password"}
            name="password"
            fullWidth
            variant="outlined"
            margin="normal"
            value={form.password || ""}
            onChange={handleChange}
            helperText={formErrors.password}
            error={Boolean(formErrors.password)}
          /> */}
          <TextField
            label="Title"
            name="position"
            fullWidth
            variant="outlined"
            margin="normal"
            value={form.position || ""}
            onChange={handleChange}
            helperText={formErrors.position}
            error={Boolean(formErrors.position)}
          />
          <input
            name="file"
            onChange={(e) => changeAvatar(e, form)}
            accept="image/*"
            id="file-upload"
            type="file"
            className={styles.input}
          />
          <div className={styles.fileUpload}>
            <label htmlFor="file-upload">
              <Avatar src={profilePictureTempUrl || _.get(form, "profilePicture")}>
                <Icon path={mdiAccount} size={1} />
              </Avatar>
            </label>
            <div>Profile Picture</div>
          </div>

          {dialogUser && (
            <div style={{ margin: "10px 0" }}>
              <Typography
                variant="body2"
                style={{
                  display: "flex",
                  alignItems: "center",
                  color: isPayingUser ? "green" : "inherit",
                }}
              >
                {isPayingUser ? (
                  <>
                    <Icon path={mdiCreditCardOutline} size={0.8} style={{ marginRight: "4px", color: "green" }} />
                    This user has an active payment method
                  </>
                ) : (
                  "No payment method on file"
                )}
              </Typography>
            </div>
          )}

          <SelectDepartment
            plans={plans}
            name="plan"
            handleChange={handleChange}
            value={form.plan}
            helperText="Which department does this user belong to?"
            multiple
          />
          <FormControl fullWidth variant="outlined" margin="normal">
            <InputLabel>Authorization</InputLabel>
            <Select name="auth" value={form.auth || ""} onChange={handleChange} label="Authorization">
              {authOptions.map((option, idx) => {
                return (
                  <MenuItem key={idx} onClick={handleChange} value={option} disabled={form.auth === option}>
                    {_.startCase(option)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {form.notificationSettings &&
            Object.keys(form.notificationSettings).map((setting, idx) => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "0 10px 10px 0",
                  maxWidth: "500px",
                }}
                key={idx}
              >
                <Typography variant="subtitle1">{_.startCase(setting)}</Typography>
                {_.get(form, `notificationSettings.${setting}.frequency`, "N/A") !== "N/A" && (
                  <Select
                    value={_.get(form, `notificationSettings.${setting}.frequency`, "N/A")}
                    onChange={handleChange}
                    name={`notificationSettings.${setting}.frequency`}
                    label={"Frequency"}
                  >
                    {NOTIFICATION_FREQUENCY.map((value) => {
                      return (
                        <MenuItem key={value} value={value}>
                          {_.startCase(value)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                )}
                <Switch
                  checked={_.get(form, `notificationSettings.${setting}.enabled`, false)}
                  onChange={handleToggleCheckBox}
                  name={`notificationSettings.${setting}.enabled`}
                />
              </div>
            ))}
          {/* <Menu icon="arrow">
              {authOptions.map((option) => {
                return (
                  <MenuItem onClick={handleChangeAuth(option)} disabled={auth === option}>
                    Change authorization to {_.startCase(option)}
                  </MenuItem>
                );
              })}
            </Menu> */}
          {invoice && !invoiceDataLoading && !dialogUser && (
            <Typography style={{ textAlign: "end", fontWeight: "bold" }}>
              Next Balance Preview: ${(invoice.total / 100).toLocaleString()} {invoice.currency.toUpperCase()}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={dialogUser ? handleClose : () => handleCreateUserCancel(form)} variant="outlined">
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit} variant="contained" disabled={dialogUser ? false : invoiceDataLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddEditUserDialog;
